import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FileText, DollarSign, TrendingUp, Package, Users, ChevronDown, Search, X, MapPin } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import KPICard from 'components/common/KPICard';
import DataTable from 'components/common/DataTable';
import EntradasNFTable from './Components/EntradasNFTable';
import CustomPieChart from 'components/common/CustomPieChart';
import CustomBarChart from 'components/common/CustomBarChart';
import EnhancedLegend from 'components/common/EnhancedLegend';
import Pagination from 'components/Pagination';
import produtosUsadosOSService from 'features/estoque/services/produtosUsadosOSService';
import { DateRangeFilterBlur } from 'components/filters';
import { RefreshCw } from 'lucide-react';
import NotificationToast from 'components/common/NotificationToast';
import PeriodoDisplay from '../PeriodoDisplay';
  // Filtro de data (mês até hoje por padrão)
  // const today = new Date();
  // const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  // const pad = (n) => String(n).padStart(2, '0');
  // const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  // Removido do topo: usar estado dentro do componente
  // const [range, setRange] = useState({ presetId: 'mtd', start: toISO(startOfMonth), end: toISO(today) });

const EntradasNFEstoque = () => {
  // Filtro de data (mês até hoje por padrão)
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const pad = (n) => String(n).padStart(2, '0');
  const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const [range, setRange] = useState({ presetId: 'mtd', start: toISO(startOfMonth), end: toISO(today) });
  const [tipoAtivo, setTipoAtivo] = useState('produtos'); // 'produtos' ou 'servicos'
  const [dadosProdutos, setDadosProdutos] = useState({});
  const [dadosServicos, setDadosServicos] = useState({});
  const [loading, setLoading] = useState(false);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  
  // Usar loading interno
  const isLoading = loading;
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [classificacaoRanking, setClassificacaoRanking] = useState('produtos'); // 'produtos' ou 'fornecedores'
  const [limparCacheLoading, setLimparCacheLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [subgruposSelecionadosProdutos, setSubgruposSelecionadosProdutos] = useState([]);
  const [subgruposSelecionadosServicos, setSubgruposSelecionadosServicos] = useState([]);
  const [subgruposSelecionadosDraftProdutos, setSubgruposSelecionadosDraftProdutos] = useState([]);
  const [subgruposSelecionadosDraftServicos, setSubgruposSelecionadosDraftServicos] = useState([]);
  const [cidadesSelecionadasProdutos, setCidadesSelecionadasProdutos] = useState([]);
  const [cidadesSelecionadasServicos, setCidadesSelecionadasServicos] = useState([]);
  const [cidadesSelecionadasDraftProdutos, setCidadesSelecionadasDraftProdutos] = useState([]);
  const [cidadesSelecionadasDraftServicos, setCidadesSelecionadasDraftServicos] = useState([]);
  const [dropdownSubgruposAberto, setDropdownSubgruposAberto] = useState(false);
  const [dropdownCidadesAberto, setDropdownCidadesAberto] = useState(false);
  const [buscaSubgrupo, setBuscaSubgrupo] = useState('');
  const [buscaCidade, setBuscaCidade] = useState('');
  const dropdownSubgruposRef = useRef(null);
  const dropdownCidadesRef = useRef(null);
  const didInitSubgrupoEffect = useRef(false);
  const rangeRef = useRef(range);
  const pendingRequestsRef = useRef(0);
  const produtosRequestIdRef = useRef(0);
  const servicosRequestIdRef = useRef(0);

  useEffect(() => {
    rangeRef.current = range;
  }, [range]);

  const iniciarRequisicao = useCallback(() => {
    pendingRequestsRef.current += 1;
    setLoading(true);
  }, []);

  const finalizarRequisicao = useCallback(() => {
    pendingRequestsRef.current = Math.max(0, pendingRequestsRef.current - 1);
    if (pendingRequestsRef.current === 0) {
      setLoading(false);
    }
  }, []);
  
  // Função para limpar cache de ambos endpoints e recarregar
  const limparCacheERecarregar = async () => {
    setLimparCacheLoading(true);
    try {
      const params = {
        data_inicio: range.start,
        data_fim: range.end
      };
      
      // Limpar cache de produtos
      const responseProdutos = await produtosUsadosOSService.limparCacheGenerico('entradas-nf-produtos', params);
      // Limpar cache de serviços
      const responseServicos = await produtosUsadosOSService.limparCacheGenerico('entradas-nf-servicos', params);
      
      // Verificar sucesso
      if (responseProdutos?.success && responseServicos?.success) {
        const message = 'Dados recarregados com sucesso!';
        setNotification({ message, type: 'success' });
        
        // Recarregar ambos
        await carregarDadosProdutos();
        await carregarDadosServicos();
      } else {
        const errorMsg = responseProdutos?.message || responseServicos?.message || 'Erro ao recarregar dados';
        setNotification({ message: errorMsg, type: 'error' });
      }
    } catch (error) {
      console.error('❌ Erro ao recarregar dados:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao recarregar dados';
      setNotification({ message: errorMessage, type: 'error' });
    } finally {
      setLimparCacheLoading(false);
    }
  };


  // Carregar dados de produtos
  const carregarDadosProdutos = useCallback(async (rangeOverride = null, selectedSubgroupsOverride = null, selectedCitiesOverride = null) => {
    const requestId = ++produtosRequestIdRef.current;
    try {
      iniciarRequisicao();
      const rangeToUse = rangeOverride || rangeRef.current;
      const selectedSubgroups = Array.isArray(selectedSubgroupsOverride)
        ? selectedSubgroupsOverride
        : subgruposSelecionadosProdutos;
      const selectedCities = Array.isArray(selectedCitiesOverride)
        ? selectedCitiesOverride
        : cidadesSelecionadasProdutos;
      const response = await produtosUsadosOSService.buscarEntradasNFProdutos({
        data_inicio: rangeToUse.start,
        data_fim: rangeToUse.end,
        subgrupos: selectedSubgroups.length > 0 ? selectedSubgroups : undefined,
        cidades: selectedCities.length > 0 ? selectedCities : undefined
      });
      
      if (response?.data && typeof response.data === 'object') {
        if (requestId !== produtosRequestIdRef.current) return;
        setDadosProdutos(response.data);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar dados de produtos:', error);
    } finally {
      if (requestId === produtosRequestIdRef.current) {
        setUltimaAtualizacao(new Date());
      }
      finalizarRequisicao();
    }
  }, [cidadesSelecionadasProdutos, finalizarRequisicao, iniciarRequisicao, subgruposSelecionadosProdutos]);

  // Carregar dados de serviços
  const carregarDadosServicos = useCallback(async (rangeOverride = null, selectedSubgroupsOverride = null, selectedCitiesOverride = null) => {
    const requestId = ++servicosRequestIdRef.current;
    try {
      iniciarRequisicao();
      const rangeToUse = rangeOverride || rangeRef.current;
      const selectedSubgroups = Array.isArray(selectedSubgroupsOverride)
        ? selectedSubgroupsOverride
        : subgruposSelecionadosServicos;
      const selectedCities = Array.isArray(selectedCitiesOverride)
        ? selectedCitiesOverride
        : cidadesSelecionadasServicos;
      const response = await produtosUsadosOSService.buscarEntradasNFServicos({
        data_inicio: rangeToUse.start,
        data_fim: rangeToUse.end,
        subgrupos: selectedSubgroups.length > 0 ? selectedSubgroups : undefined,
        cidades: selectedCities.length > 0 ? selectedCities : undefined
      });
      
      if (response?.data && typeof response.data === 'object') {
        if (requestId !== servicosRequestIdRef.current) return;
        setDadosServicos(response.data);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar dados de serviços:', error);
    } finally {
      if (requestId === servicosRequestIdRef.current) {
        setUltimaAtualizacao(new Date());
      }
      finalizarRequisicao();
    }
  }, [cidadesSelecionadasServicos, finalizarRequisicao, iniciarRequisicao, subgruposSelecionadosServicos]);

  // Carregar dados quando o intervalo muda
  useEffect(() => {
    carregarDadosProdutos(range);
    carregarDadosServicos(range);
  }, [range, carregarDadosProdutos, carregarDadosServicos]);

  useEffect(() => {
    const handleCompanyChange = () => {
      setSubgruposSelecionadosProdutos([]);
      setSubgruposSelecionadosServicos([]);
      setSubgruposSelecionadosDraftProdutos([]);
      setSubgruposSelecionadosDraftServicos([]);
      setCidadesSelecionadasProdutos([]);
      setCidadesSelecionadasServicos([]);
      setCidadesSelecionadasDraftProdutos([]);
      setCidadesSelecionadasDraftServicos([]);
      setBuscaSubgrupo('');
      setBuscaCidade('');
      setDropdownSubgruposAberto(false);
      setDropdownCidadesAberto(false);
      carregarDadosProdutos(null, [], []);
      carregarDadosServicos(null, [], []);
    };

    window.addEventListener('company-changed', handleCompanyChange);
    window.addEventListener('company-context-changed', handleCompanyChange);

    return () => {
      window.removeEventListener('company-changed', handleCompanyChange);
      window.removeEventListener('company-context-changed', handleCompanyChange);
    };
  }, [carregarDadosProdutos, carregarDadosServicos]);

  useEffect(() => {
    const selecoesIguais = (a = [], b = []) => (
      a.length === b.length && a.every((item) => b.includes(item))
    );

    const applySubgruposSeMudou = () => {
      if (tipoAtivo === 'produtos') {
        if (!selecoesIguais(subgruposSelecionadosProdutos, subgruposSelecionadosDraftProdutos)) {
          setSubgruposSelecionadosProdutos(subgruposSelecionadosDraftProdutos);
        }
        return;
      }

      if (!selecoesIguais(subgruposSelecionadosServicos, subgruposSelecionadosDraftServicos)) {
        setSubgruposSelecionadosServicos(subgruposSelecionadosDraftServicos);
      }
    };

    const handler = (e) => {
      if (dropdownSubgruposRef.current && !dropdownSubgruposRef.current.contains(e.target)) {
        if (dropdownSubgruposAberto) {
          applySubgruposSeMudou();
        }
        setDropdownSubgruposAberto(false);
      }
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [
    subgruposSelecionadosProdutos,
    subgruposSelecionadosDraftProdutos,
    subgruposSelecionadosServicos,
    subgruposSelecionadosDraftServicos,
    dropdownSubgruposAberto,
    tipoAtivo
  ]);

  useEffect(() => {
    const selecoesIguais = (a = [], b = []) => (
      a.length === b.length && a.every((item) => b.includes(item))
    );

    const applyCidadesSeMudou = () => {
      if (tipoAtivo === 'produtos') {
        if (!selecoesIguais(cidadesSelecionadasProdutos, cidadesSelecionadasDraftProdutos)) {
          setCidadesSelecionadasProdutos(cidadesSelecionadasDraftProdutos);
        }
        return;
      }

      if (!selecoesIguais(cidadesSelecionadasServicos, cidadesSelecionadasDraftServicos)) {
        setCidadesSelecionadasServicos(cidadesSelecionadasDraftServicos);
      }
    };

    const handler = (e) => {
      if (dropdownCidadesRef.current && !dropdownCidadesRef.current.contains(e.target)) {
        if (dropdownCidadesAberto) {
          applyCidadesSeMudou();
        }
        setDropdownCidadesAberto(false);
      }
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [
    cidadesSelecionadasProdutos,
    cidadesSelecionadasDraftProdutos,
    cidadesSelecionadasServicos,
    cidadesSelecionadasDraftServicos,
    dropdownCidadesAberto,
    tipoAtivo
  ]);

  useEffect(() => {
    if (!didInitSubgrupoEffect.current) {
      didInitSubgrupoEffect.current = true;
      return;
    }

    if (tipoAtivo === 'produtos') {
      carregarDadosProdutos();
      return;
    }

    carregarDadosServicos();
  }, [
    subgruposSelecionadosProdutos,
    subgruposSelecionadosServicos,
    cidadesSelecionadasProdutos,
    cidadesSelecionadasServicos,
    tipoAtivo,
    carregarDadosProdutos,
    carregarDadosServicos
  ]);



  // Removido LoadingSpinner para permitir blur do DateRangeFilterBlur

  // Dados ativos baseado no tipo selecionado
  const dadosAtivos = tipoAtivo === 'produtos' ? dadosProdutos : dadosServicos;
  const tipoLabel = tipoAtivo === 'produtos' ? 'Produtos' : 'Serviços';
  const subgruposDisponiveis = useMemo(() => dadosAtivos?.subgrupos_disponiveis || [], [dadosAtivos?.subgrupos_disponiveis]);
  const cidadesDisponiveis = useMemo(() => dadosAtivos?.cidades_disponiveis || [], [dadosAtivos?.cidades_disponiveis]);
  const subgruposSelecionadosAtivos = tipoAtivo === 'produtos' ? subgruposSelecionadosProdutos : subgruposSelecionadosServicos;
  const subgruposSelecionadosDraftAtivos = tipoAtivo === 'produtos' ? subgruposSelecionadosDraftProdutos : subgruposSelecionadosDraftServicos;
  const cidadesSelecionadasAtivas = tipoAtivo === 'produtos' ? cidadesSelecionadasProdutos : cidadesSelecionadasServicos;
  const cidadesSelecionadasDraftAtivas = tipoAtivo === 'produtos' ? cidadesSelecionadasDraftProdutos : cidadesSelecionadasDraftServicos;
  const subgruposFiltrados = useMemo(() => {
    if (!buscaSubgrupo.trim()) return subgruposDisponiveis;
    const termo = buscaSubgrupo.toLowerCase().trim();
    return subgruposDisponiveis.filter((subgrupo) => String(subgrupo).toLowerCase().includes(termo));
  }, [subgruposDisponiveis, buscaSubgrupo]);
  const cidadesFiltradas = useMemo(() => {
    if (!buscaCidade.trim()) return cidadesDisponiveis;
    const termo = buscaCidade.toLowerCase().trim();
    return cidadesDisponiveis.filter((cidade) => String(cidade).toLowerCase().includes(termo));
  }, [cidadesDisponiveis, buscaCidade]);

  // Dados para gráficos
  const kpis = dadosAtivos.kpis || {};
  const topProdutos = dadosAtivos.top_comprados?.top_10 || [];
  const topFornecedores = dadosAtivos.top_fornecedores?.top_10 || [];
  const detalhamento = dadosAtivos.detalhamento?.detalhamento || [];
  const detalhamentoCompleto = dadosAtivos.detalhamento_completo?.detalhamento || [];
  const porDocumento = dadosAtivos.por_documento?.entradas || [];
  const porOperador = dadosAtivos.por_operador?.entradas || [];
  const porMes = dadosAtivos.por_mes?.entradas || [];

  // Paginação para detalhamento
  const totalPages = Math.ceil(detalhamento.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const detalhamentoPaginado = detalhamento.slice(startIndex, startIndex + itemsPerPage);

  const columnsDetalhamento = [
    {
      key: 'ID ENTRADA',
      label: 'ID ENTRADA',
      sortable: true,
      className: 'w-[15%]'
    },
    {
      key: 'DATA',
      label: 'Data',
      sortable: true,
      className: 'w-[15%]'
    },
    {
      key: 'DOCUMENTO',
      label: 'Documento',
      sortable: true,
      className: 'w-[50%]'
    },
    {
      key: 'VALOR TOTAL',
      label: 'Valor Total',
      sortable: true,
      className: 'w-[20%] text-right',
      render: (value) => (
        <span className="font-bold text-[color:var(--status-good)] text-xs tabular-nums">
          R$ {parseFloat(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </span>
      )
    }
  ];

  const _columnsDetalhamentoCompleto = [
    {
      key: 'ID ENTRADA',
      label: 'ID',
      sortable: true
    },
    {
      key: 'DATA',
      label: 'Data',
      sortable: true
    },
    {
      key: 'FUNCIONARIO',
      label: 'Funcionário',
      sortable: true
    },
    {
      key: 'SETOR',
      label: 'Setor',
      sortable: true
    },
    {
      key: 'PRODUTO',
      label: 'Produto',
      sortable: true
    },
    {
      key: 'DOCUMENTO',
      label: 'Documento',
      sortable: true
    },
    {
      key: 'FORNECEDOR',
      label: 'Fornecedor',
      sortable: true
    },
    {
      key: 'QUANTIDADE',
      label: 'Quantidade',
      sortable: true,
      className: 'text-right',
      render: (value) => (
        <span className="font-medium text-[color:var(--text-primary)]">
          {Math.round(Number(value || 0)).toLocaleString('pt-BR', { 
            minimumFractionDigits: 0, 
            maximumFractionDigits: 0 
          })}
        </span>
      )
    },
    {
      key: 'VALOR UNITÁRIO',
      label: 'Valor Unitário',
      sortable: true,
      className: 'text-right',
      render: (value) => (
        <span className="font-bold text-[color:var(--accent)]">
          R$ {parseFloat(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </span>
      )
    },
    {
      key: 'VALOR ITEM',
      label: 'Valor Item',
      sortable: true,
      className: 'text-right',
      render: (value) => (
        <span className="font-bold text-[color:var(--text-primary)]">
          R$ {parseFloat(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </span>
      )
    },
    {
      key: 'VALOR NF',
      label: 'Valor NF',
      sortable: true,
      className: 'text-right',
      render: (value) => (
        <span className="font-bold text-[color:var(--status-good)] tabular-nums">
          R$ {parseFloat(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </span>
      )
    }
  ];

  // Dados para gráficos
  const dadosGraficoDocumento = porDocumento.slice(0, 10).map(item => ({
    name: item.DOCUMENTO || 'NÃO INFORMADO',
    value: item.TOTAL
  }));

  const dadosGraficoOperador = porOperador.slice(0, 10).map(item => ({
    name: item.OPERADOR || 'NÃO INFORMADO',
    value: item.TOTAL
  }));

  const dadosGraficoMes = porMes
    .map(item => ({
      name: item.DATA,
      value: item.TOTAL
    }))
    .sort((a, b) => {
      // Converter formato MM/YYYY para Date para ordenação correta considerando ano e mês
      const parseData = (dataStr) => {
        if (!dataStr) return new Date(0);
        // Formato pode ser "01/2026" ou "2026-01" ou similar
        if (dataStr.includes('/')) {
          const [mes, ano] = dataStr.split('/');
          return new Date(parseInt(ano), parseInt(mes) - 1, 1);
        } else if (dataStr.includes('-')) {
          const [ano, mes] = dataStr.split('-');
          return new Date(parseInt(ano), parseInt(mes) - 1, 1);
        }
        return new Date(0);
      };
      
      const dateA = parseData(a.name);
      const dateB = parseData(b.name);
      return dateA - dateB;
    });

  // Dados para gráficos de pizza dos rankings
  const dadosGraficoProdutos = topProdutos.slice(0, 10).map(item => ({
    name: item.PRODUTO,
    value: item.TOTAL
  }));

  const dadosGraficoFornecedores = topFornecedores.slice(0, 10).map(item => ({
    name: item.FORNECEDOR,
    value: item.TOTAL
  }));

  const chartTheme = useMemo(() => ({
    gridStroke: 'var(--border)',
    axisStroke: 'var(--border)',
    tickColor: 'var(--text-muted)',
    barFill: 'var(--accent)',
    tooltipBg: 'var(--surface)',
    tooltipBorder: 'var(--border)',
    tooltipText: 'var(--text-primary)',
    tooltipLabel: 'var(--text-muted)'
  }), []);

  const rankingLegendColors = useMemo(() => ([
    'var(--accent)',
    'color-mix(in srgb, var(--accent) 82%, var(--surface) 18%)',
    'color-mix(in srgb, var(--accent) 70%, var(--surface) 30%)',
    'color-mix(in srgb, var(--accent) 58%, var(--surface) 42%)',
    'color-mix(in srgb, var(--accent) 46%, var(--surface) 54%)',
    'color-mix(in srgb, var(--accent) 36%, var(--surface) 64%)',
    'color-mix(in srgb, var(--accent) 28%, var(--surface) 72%)',
    'color-mix(in srgb, var(--accent) 22%, var(--surface) 78%)',
    'color-mix(in srgb, var(--accent) 16%, var(--surface) 84%)',
    'color-mix(in srgb, var(--accent) 10%, var(--surface) 90%)'
  ]), []);

  const toggleSubgrupo = (subgrupo) => {
    const setDraft = tipoAtivo === 'produtos' ? setSubgruposSelecionadosDraftProdutos : setSubgruposSelecionadosDraftServicos;
    setDraft((prev) => {
      if (prev.includes(subgrupo)) {
        return prev.filter((item) => item !== subgrupo);
      }
      return [...prev, subgrupo];
    });
  };

  const limparSubgrupos = () => {
    if (tipoAtivo === 'produtos') {
      setSubgruposSelecionadosDraftProdutos([]);
    } else {
      setSubgruposSelecionadosDraftServicos([]);
    }
    setBuscaSubgrupo('');
  };

  const toggleCidade = (cidade) => {
    const setDraft = tipoAtivo === 'produtos' ? setCidadesSelecionadasDraftProdutos : setCidadesSelecionadasDraftServicos;
    setDraft((prev) => {
      if (prev.includes(cidade)) {
        return prev.filter((item) => item !== cidade);
      }
      return [...prev, cidade];
    });
  };

  const limparCidades = () => {
    if (tipoAtivo === 'produtos') {
      setCidadesSelecionadasDraftProdutos([]);
    } else {
      setCidadesSelecionadasDraftServicos([]);
    }
    setBuscaCidade('');
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          duration={4000}
          onClose={() => setNotification(null)}
        />
      )}
      <div className="rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)]/95 shadow-[0_8px_28px_rgba(15,23,42,0.08)] px-4 py-4 sm:px-5 sm:py-5">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-sm uppercase tracking-wide font-semibold text-[color:var(--text-muted)]">Período</h3>
          <PeriodoDisplay start={range.start} end={range.end} />
          {loading ? (
            <div className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium bg-[color:var(--surface-muted)] text-[color:var(--accent)]">
              <svg className="h-4 w-4 mr-1 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
              Atualizando...
            </div>
          ) : (
            ultimaAtualizacao && (
              <div className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-[color:var(--surface-muted)] text-[color:var(--text-muted)]">Atualizado: {ultimaAtualizacao.toLocaleTimeString('pt-BR')}</div>
            )
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={limparCacheERecarregar}
            disabled={limparCacheLoading || loading}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-3.5 text-sm font-medium text-[color:var(--text-primary)] shadow-sm transition-all duration-200 hover:brightness-[1.03] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 focus:ring-offset-[color:var(--surface)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`h-4 w-4 ${limparCacheLoading ? 'animate-spin' : ''}`} />
            {limparCacheLoading ? 'Recarregando...' : 'Recarregar Dados'}
          </button>
          <div ref={dropdownSubgruposRef} className="relative inline-block">
            <button
              type="button"
              onClick={() => {
                if (dropdownSubgruposAberto) {
                  const selecoesIguais = subgruposSelecionadosAtivos.length === subgruposSelecionadosDraftAtivos.length
                    && subgruposSelecionadosAtivos.every((item) => subgruposSelecionadosDraftAtivos.includes(item));
                  if (!selecoesIguais) {
                    if (tipoAtivo === 'produtos') {
                      setSubgruposSelecionadosProdutos(subgruposSelecionadosDraftAtivos);
                    } else {
                      setSubgruposSelecionadosServicos(subgruposSelecionadosDraftAtivos);
                    }
                  }
                  setDropdownSubgruposAberto(false);
                  return;
                }
                if (tipoAtivo === 'produtos') {
                  setSubgruposSelecionadosDraftProdutos(subgruposSelecionadosProdutos);
                } else {
                  setSubgruposSelecionadosDraftServicos(subgruposSelecionadosServicos);
                }
                setDropdownSubgruposAberto(true);
              }}
              className={`w-full sm:w-auto inline-flex h-10 items-center gap-2 rounded-lg border px-3.5 text-sm font-medium shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 focus:ring-offset-[color:var(--surface)] ${
                (dropdownSubgruposAberto ? subgruposSelecionadosDraftAtivos : subgruposSelecionadosAtivos).length > 0
                  ? 'border-[color:var(--accent)] bg-[color:var(--accent)] text-white'
                  : 'border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]'
              }`}
            >
              <span className="truncate">
                {(dropdownSubgruposAberto ? subgruposSelecionadosDraftAtivos : subgruposSelecionadosAtivos).length === 0
                  ? 'Filtrar por Subgrupo'
                  : `${(dropdownSubgruposAberto ? subgruposSelecionadosDraftAtivos : subgruposSelecionadosAtivos).length} subgrupo(s) selecionado(s)`}
              </span>
              {(dropdownSubgruposAberto ? subgruposSelecionadosDraftAtivos : subgruposSelecionadosAtivos).length > 0 && (
                <span className="inline-flex items-center rounded-full bg-[color:var(--accent)] px-2 py-0.5 text-xs font-medium text-white">
                  {(dropdownSubgruposAberto ? subgruposSelecionadosDraftAtivos : subgruposSelecionadosAtivos).length}
                </span>
              )}
              <ChevronDown className="h-4 w-4 text-[color:var(--text-muted)]" />
            </button>

            {dropdownSubgruposAberto && (
              <div className="absolute z-50 mt-2 w-72 rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] shadow-[0_16px_40px_rgba(2,6,23,0.28)] focus:outline-none right-0">
                <div className="p-3 border-b border-[color:var(--border)] bg-[color:var(--surface-muted)]/35 rounded-t-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold tracking-tight text-[color:var(--text-primary)]">Filtrar por Subgrupo</span>
                    {subgruposSelecionadosDraftAtivos.length > 0 && (
                      <button
                        onClick={limparSubgrupos}
                        className="text-xs font-medium text-[color:var(--accent)] hover:opacity-80"
                      >
                        Limpar
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--text-muted)]" />
                    <input
                      type="text"
                      value={buscaSubgrupo}
                      onChange={(e) => setBuscaSubgrupo(e.target.value)}
                      placeholder="Buscar subgrupo..."
                      className="w-full pl-8 pr-2 py-2 text-sm border border-[color:var(--border)] rounded-lg bg-[color:var(--surface)] text-[color:var(--text-primary)] placeholder-[color:var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
                      onClick={(e) => e.stopPropagation()}
                    />
                    {buscaSubgrupo && (
                      <button
                        onClick={() => setBuscaSubgrupo('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
                        type="button"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="max-h-64 overflow-auto p-2">
                  {subgruposFiltrados.length > 0 ? (
                    subgruposFiltrados.map((subgrupo) => {
                      const isSelected = subgruposSelecionadosDraftAtivos.includes(subgrupo);
                      return (
                        <label
                          key={subgrupo}
                          className={`flex items-center px-3 py-2 text-sm hover:bg-[color:var(--surface-muted)] cursor-pointer rounded-lg transition-colors ${
                            isSelected ? 'bg-[color:var(--surface-muted)]' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSubgrupo(subgrupo)}
                            className="h-4 w-4 text-[color:var(--accent)] focus:ring-[color:var(--accent)] border-[color:var(--border)] rounded"
                          />
                          <span className={`ml-2 flex-1 ${isSelected ? 'font-medium text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)]'}`}>
                            {subgrupo}
                          </span>
                        </label>
                      );
                    })
                  ) : (
                    <div className="px-3 py-2 text-sm text-[color:var(--text-muted)]">
                      {buscaSubgrupo ? 'Nenhum subgrupo encontrado' : 'Nenhum subgrupo disponível'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div ref={dropdownCidadesRef} className="relative inline-block">
            <button
              type="button"
              onClick={() => {
                if (dropdownCidadesAberto) {
                  const selecoesIguais = cidadesSelecionadasAtivas.length === cidadesSelecionadasDraftAtivas.length
                    && cidadesSelecionadasAtivas.every((item) => cidadesSelecionadasDraftAtivas.includes(item));
                  if (!selecoesIguais) {
                    if (tipoAtivo === 'produtos') {
                      setCidadesSelecionadasProdutos(cidadesSelecionadasDraftAtivas);
                    } else {
                      setCidadesSelecionadasServicos(cidadesSelecionadasDraftAtivas);
                    }
                  }
                  setDropdownCidadesAberto(false);
                  return;
                }
                if (tipoAtivo === 'produtos') {
                  setCidadesSelecionadasDraftProdutos(cidadesSelecionadasProdutos);
                } else {
                  setCidadesSelecionadasDraftServicos(cidadesSelecionadasServicos);
                }
                setDropdownCidadesAberto(true);
              }}
              className={`w-full sm:w-auto inline-flex h-10 items-center gap-2 rounded-lg border px-3.5 text-sm font-medium shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 focus:ring-offset-[color:var(--surface)] ${
                (dropdownCidadesAberto ? cidadesSelecionadasDraftAtivas : cidadesSelecionadasAtivas).length > 0
                  ? 'border-[color:var(--accent)] bg-[color:var(--accent)] text-white'
                  : 'border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]'
              }`}
            >
              <MapPin className="h-4 w-4" />
              <span className="truncate">
                {(dropdownCidadesAberto ? cidadesSelecionadasDraftAtivas : cidadesSelecionadasAtivas).length === 0
                  ? 'Filtrar por Cidade'
                  : `${(dropdownCidadesAberto ? cidadesSelecionadasDraftAtivas : cidadesSelecionadasAtivas).length} cidade(s) selecionada(s)`}
              </span>
              {(dropdownCidadesAberto ? cidadesSelecionadasDraftAtivas : cidadesSelecionadasAtivas).length > 0 && (
                <span className="inline-flex items-center rounded-full bg-[color:var(--accent)] px-2 py-0.5 text-xs font-medium text-white">
                  {(dropdownCidadesAberto ? cidadesSelecionadasDraftAtivas : cidadesSelecionadasAtivas).length}
                </span>
              )}
              <ChevronDown className="h-4 w-4 text-[color:var(--text-muted)]" />
            </button>

            {dropdownCidadesAberto && (
              <div className="absolute z-50 mt-2 w-72 rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] shadow-[0_16px_40px_rgba(2,6,23,0.28)] focus:outline-none right-0">
                <div className="p-3 border-b border-[color:var(--border)] bg-[color:var(--surface-muted)]/35 rounded-t-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold tracking-tight text-[color:var(--text-primary)]">Filtrar por Cidade</span>
                    {cidadesSelecionadasDraftAtivas.length > 0 && (
                      <button
                        onClick={limparCidades}
                        className="text-xs font-medium text-[color:var(--accent)] hover:opacity-80"
                      >
                        Limpar
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--text-muted)]" />
                    <input
                      type="text"
                      value={buscaCidade}
                      onChange={(e) => setBuscaCidade(e.target.value)}
                      placeholder="Buscar cidade..."
                      className="w-full pl-8 pr-2 py-2 text-sm border border-[color:var(--border)] rounded-lg bg-[color:var(--surface)] text-[color:var(--text-primary)] placeholder-[color:var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
                      onClick={(e) => e.stopPropagation()}
                    />
                    {buscaCidade && (
                      <button
                        onClick={() => setBuscaCidade('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
                        type="button"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="max-h-64 overflow-auto p-2">
                  {cidadesFiltradas.length > 0 ? (
                    cidadesFiltradas.map((cidade) => {
                      const isSelected = cidadesSelecionadasDraftAtivas.includes(cidade);
                      return (
                        <label
                          key={cidade}
                          className={`flex items-center px-3 py-2 text-sm hover:bg-[color:var(--surface-muted)] cursor-pointer rounded-lg transition-colors ${
                            isSelected ? 'bg-[color:var(--surface-muted)]' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleCidade(cidade)}
                            className="h-4 w-4 text-[color:var(--accent)] focus:ring-[color:var(--accent)] border-[color:var(--border)] rounded"
                          />
                          <span className={`ml-2 flex-1 ${isSelected ? 'font-medium text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)]'}`}>
                            {cidade}
                          </span>
                        </label>
                      );
                    })
                  ) : (
                    <div className="px-3 py-2 text-sm text-[color:var(--text-muted)]">
                      {buscaCidade ? 'Nenhuma cidade encontrada' : 'Nenhuma cidade disponível'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="h-10">
            <DateRangeFilterBlur
              value={range}
              loading={loading}
              onChange={(r) => {
                setRange(r);
              }}
            />
          </div>
        </div>
      </div>
      </div>
      {/* Toggle para alternar entre Produtos e Serviços */}
      <div className="bg-[color:var(--surface)] rounded-xl border border-[color:var(--border)] shadow-[0_8px_28px_rgba(15,23,42,0.08)] p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <h3 className="text-lg font-semibold text-[color:var(--text-primary)]">
            Tipo de Entrada
          </h3>
          <div className="flex items-center justify-center sm:justify-end space-x-4">
            <span className={`text-sm font-medium ${tipoAtivo === 'produtos' ? 'text-[color:var(--accent)]' : 'text-[color:var(--text-muted)]'}`}>
              Produtos
            </span>
      <button
        onClick={() => setTipoAtivo(tipoAtivo === 'produtos' ? 'servicos' : 'produtos')}
        className={`relative inline-flex h-6 w-11 items-center rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 ${
          tipoAtivo === 'servicos'
            ? 'bg-[color:var(--accent)] border-transparent'
            : 'bg-[color:var(--surface-muted)] border-[color:var(--border)]'
        }`}
        aria-pressed={tipoAtivo === 'servicos'}
      >
        <span
          className={`${
            tipoAtivo === 'servicos' ? 'translate-x-6' : 'translate-x-1'
          } inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-1 ring-black/10 transition-transform`}
        />
      </button>
            <span className={`text-sm font-medium ${tipoAtivo === 'servicos' ? 'text-[color:var(--accent)]' : 'text-[color:var(--text-muted)]'}`}>
              Serviços
            </span>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title={`Total de NF (${tipoLabel})`}
          value={kpis.total_nf?.toLocaleString('pt-BR') || '0'}
          icon={FileText}
          color="blue"
          className="min-h-[132px]"
        />
        <KPICard
          title={`Total de Entradas (${tipoLabel})`}
          value={parseFloat(kpis.total_entradas || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          icon={Package}
          color="green"
          className="min-h-[132px]"
        />
        <KPICard
          title={`Valor Total (${tipoLabel})`}
          value={`R$ ${parseFloat(kpis.total_valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          icon={DollarSign}
          color="purple"
          className="min-h-[132px]"
        />
        <KPICard
          title={`Média por NF (${tipoLabel})`}
          value={`R$ ${(parseFloat(kpis.total_valor || 0) / (kpis.total_nf || 1)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          icon={TrendingUp}
          color="orange"
          className="min-h-[132px]"
        />
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
        <CustomBarChart
          data={dadosGraficoDocumento}
          title={`Entradas por Documento (${tipoLabel})`}
          dataKey="value"
          nameKey="name"
          color="var(--accent)"
          maxHeight="max-h-80"
          allowLabelWrap={true}
          maxLabelLines={3}
        />
        <CustomBarChart
          data={dadosGraficoOperador}
          title={`Entradas por Operador (${tipoLabel})`}
          dataKey="value"
          nameKey="name"
          color="var(--status-good)"
          maxHeight="max-h-80"
          allowLabelWrap={true}
          maxLabelLines={3}
        />
      </div>

      {/* Gráfico de Barras - Por Mês */}
      <div className="bg-[color:var(--surface)] rounded-xl border border-[color:var(--border)] shadow-[0_8px_28px_rgba(15,23,42,0.08)] p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold tracking-tight text-[color:var(--text-primary)] mb-4">
          Entradas por Mês ({tipoLabel})
          <span className="ml-2 text-sm text-[color:var(--text-muted)]">
            ({dadosGraficoMes.length} registros)
          </span>
        </h3>
        
        {dadosGraficoMes.length > 0 ? (
          <div className="h-80 sm:h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={dadosGraficoMes}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 12,
                }}
              >
                <CartesianGrid stroke={chartTheme.gridStroke} strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45}
                  textAnchor="end"
                  height={72}
                  fontSize={11}
                  interval={0}
                  tickMargin={8}
                  stroke={chartTheme.axisStroke}
                  tick={{ fill: chartTheme.tickColor }}
                />
                <YAxis
                  stroke={chartTheme.axisStroke}
                  tick={{ fill: chartTheme.tickColor }}
                />
                <Tooltip 
                  formatter={(value, _name) => [value.toLocaleString('pt-BR'), 'Entradas']}
                  labelFormatter={(label) => `Mês: ${label}`}
                  contentStyle={{
                    backgroundColor: chartTheme.tooltipBg,
                    border: `1px solid ${chartTheme.tooltipBorder}`,
                    borderRadius: '10px',
                    color: chartTheme.tooltipText,
                    boxShadow: '0 8px 28px rgba(0, 0, 0, 0.25)'
                  }}
                  itemStyle={{ color: chartTheme.tooltipText, fontWeight: 600 }}
                  labelStyle={{ color: chartTheme.tooltipLabel }}
                  cursor={{ fill: 'rgba(148, 163, 184, 0.16)' }}
                />
                <Bar 
                  dataKey="value" 
                  fill={chartTheme.barFill}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-center py-12 text-[color:var(--text-muted)]">
            <Package className="w-12 h-12 mx-auto mb-4 text-[color:var(--text-muted)]/50" />
            <p>Nenhum dado disponível para este período</p>
          </div>
        )}
      </div>

      {/* Rankings com Classificação */}
      <div className="bg-[color:var(--surface)] rounded-xl border border-[color:var(--border)] shadow-[0_8px_28px_rgba(15,23,42,0.08)] p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold tracking-tight text-[color:var(--text-primary)] mb-4 flex items-center">
          <Package className="w-5 h-5 mr-2 text-[color:var(--accent)]" />
          Classificação de Rankings
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <button
            onClick={() => setClassificacaoRanking('produtos')}
            className={`flex items-center justify-center p-3 rounded-lg text-sm font-medium transition-colors duration-200 border
              ${classificacaoRanking === 'produtos'
                ? 'bg-[color:var(--accent)] text-white border-[color:var(--accent)]'
                : 'bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] border-[color:var(--border)] hover:brightness-[1.03]'
              }`}
          >
            <Package className={`w-4 h-4 mr-2 ${classificacaoRanking === 'produtos' ? 'text-white' : 'text-[color:var(--accent)]'}`} />
            {tipoAtivo === 'produtos' ? 'Produtos' : 'Serviços'}
          </button>
          <button
            onClick={() => setClassificacaoRanking('fornecedores')}
            className={`flex items-center justify-center p-3 rounded-lg text-sm font-medium transition-colors duration-200 border
              ${classificacaoRanking === 'fornecedores'
                ? 'bg-[color:var(--accent)] text-white border-[color:var(--accent)]'
                : 'bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] border-[color:var(--border)] hover:brightness-[1.03]'
              }`}
          >
            <Users className={`w-4 h-4 mr-2 ${classificacaoRanking === 'fornecedores' ? 'text-white' : 'text-[color:var(--accent)]'}`} />
            Fornecedores
          </button>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
          {/* Lista de Rankings (mesma fonte da legenda do gráfico) */}
          <div className="flex flex-col">
            <h4 className="text-sm sm:text-base font-semibold text-[color:var(--text-primary)] mb-4">
              {classificacaoRanking === 'produtos' 
                ? `${tipoAtivo === 'produtos' ? 'Produtos' : 'Serviços'} Mais Comprados`
                : 'Top Fornecedores'
              }
              <span className="ml-2 text-[color:var(--text-muted)] text-sm">
                ({classificacaoRanking === 'produtos' ? topProdutos.length : topFornecedores.length} registros)
              </span>
            </h4>
            <div className="flex-1 min-h-[400px] sm:min-h-[500px] max-h-[500px] overflow-y-auto pr-1">
              <EnhancedLegend
                data={classificacaoRanking === 'produtos' ? dadosGraficoProdutos : dadosGraficoFornecedores}
                dataKey="value"
                nameKey="name"
                colors={rankingLegendColors}
                maxItems={10}
                showToggle={false}
                sortDescending={true}
              />
            </div>
          </div>

          {/* Gráfico de Pizza */}
          <div className="flex flex-col">
            <h4 className="text-sm sm:text-base font-semibold text-[color:var(--text-primary)] mb-4">
              Distribuição {classificacaoRanking === 'produtos' 
                ? `por ${tipoAtivo === 'produtos' ? 'Produto' : 'Serviço'}`
                : 'por Fornecedor'
              }
            </h4>
            <div className="flex-1 min-h-[400px] sm:min-h-[500px]">
              <CustomPieChart
                data={classificacaoRanking === 'produtos' ? dadosGraficoProdutos : dadosGraficoFornecedores}
                title=""
                dataKey="value"
                nameKey="name"
                height={400}
                showLegend={false}
                sortDescending={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tabelas de Detalhamento */}
      <div className="space-y-4 sm:space-y-6">
        <div>
          <h3 className="text-base sm:text-lg font-semibold tracking-tight text-[color:var(--text-primary)] mb-4">
            Detalhamento Básico ({tipoLabel})
          </h3>
          <div className="overflow-x-auto">
            <DataTable
              data={detalhamentoPaginado}
              columns={columnsDetalhamento}
              loading={isLoading}
              maxHeight="max-h-80 sm:max-h-96"
              emptyMessage="Nenhuma entrada encontrada"
            />
          </div>
          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={setItemsPerPage}
                totalItems={detalhamento.length}
              />
            </div>
          )}
        </div>

        <div>
          <h3 className="text-base sm:text-lg font-semibold tracking-tight text-[color:var(--text-primary)] mb-4">
            Detalhamento Completo ({tipoLabel})
          </h3>
          <div className="overflow-x-hidden">
            <EntradasNFTable
              data={detalhamentoCompleto}
              loading={isLoading}
              maxHeight="max-h-80 sm:max-h-96"
              emptyMessage="Nenhuma entrada encontrada"
              showProductFilter
              showSummaryTotals
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EntradasNFEstoque;
